import { useState, useEffect } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";
import { useUser, useUpdateExpense, useCreateExpense, useExpenses, useDeleteExpense, useGetPermissions } from "../Use-auth";
import { X, Pencil, Trash2, HandCoins, CalendarRange, Eye, } from "lucide-react";
import DateRangePicker from "../ui/DateRangePicker";
import { format, } from "date-fns";
import Pagination from "../ui/Pagination";
import SkeletonPage from "./SkeletonPage ";
import PermissionPopup from "./PermissionPopup";

const InfoRow = ({ label, value, className }) => (
    <tr className="text-[10px] sm:text-[12px]">
        <td className="px-4 py-2 font-medium text-gray-600 w-1/3">
            {label}
        </td>

        <td className="px-2 py-1 sm:px-2 sm:py-2 text-gray-800 text-right zx:text-left">
            {value || "-"}
        </td>
    </tr >
);

export default function ExpensesPage() {
    const { data: user } = useUser();
    const { data: existingPermissions, refetch } = useGetPermissions();
    const { data: expensesData, isLoading: fetching } = useExpenses();
    const createExpense = useCreateExpense();
    const updateExpense = useUpdateExpense();
    const deleteExpense = useDeleteExpense();
    const [search, setSearch] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [editingExpenseId, setEditingExpenseId] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState("ALL");
    const [report, setReport] = useState("");
    const [expenseDate, setExpenseDate] = useState("");
    const [merchant, setMerchant] = useState("");
    const [category, setCategory] = useState("");
    const [amount, setAmount] = useState('');
    const [quantity, setQuantity] = useState('');
    const [weight, setWeight] = useState('');
    const [description, setDescription] = useState("");
    const [paidBy, setPaidBy] = useState("");
    const [status, setStatus] = useState("UnPaid");
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [previewExpenses, setPreviewExpenses] = useState(null);
    const isAdmin = user?.department === "Admin";

    const currentUserPermissions = isAdmin
        ? {
            hr: {
                expense: true,
            },
        }
        : existingPermissions?.find(p => p.userId === user?.userId);

    const canViewHome =
        isAdmin || currentUserPermissions?.hr?.expense === true

    const [dateRange, setDateRange] = useState({
        startDate: new Date(),
        endDate: new Date()
    });
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const formsPerPage = 20;

    const isSameOrInRange = (date, start, end) => {
        const d = new Date(date);
        const s = new Date(start);
        const e = new Date(end);

        d.setHours(0, 0, 0, 0);
        s.setHours(0, 0, 0, 0);
        e.setHours(23, 59, 59, 999);

        return d >= s && d <= e;
    };

    const expenses = expensesData || [];

    const handleOpen = () => {
        setEditingExpenseId(null);
        setReport("");
        setExpenseDate("");
        setMerchant("");
        setCategory("");
        setAmount("");
        setQuantity("");
        setWeight("");
        setDescription("");
        setPaidBy("");
        setStatus("UnPaid");
        setShowPopup(true);
    };

    const handleEdit = (exp) => {
        setEditingExpenseId(exp._id);
        setReport(exp.reportName || "");
        setExpenseDate(exp.expenseDate || "");
        setMerchant(exp.merchant || "");
        setCategory(exp.category || "");
        setAmount(exp.amount || "");
        setQuantity(exp.quantity || "");
        setWeight(exp.weight || "");
        setDescription(exp.description || "");
        setPaidBy(exp.paidBy || "");
        setStatus(exp.status || "UnPaid");
        setShowPopup(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccessMsg("");
        setErrorMsg("");

        const expenseData = {
            userId: user?.userId,
            userName: user?.fullName,
            report,
            expenseDate,
            merchant,
            category,
            weight,
            quantity,
            amount,
            description,
            paidBy,
            status,
        };

        try {
            if (editingExpenseId) {
                await updateExpense.mutateAsync({ id: editingExpenseId, expenseData });
                setSuccessMsg("Expense updated successfully");
            } else {
                await createExpense.mutateAsync(expenseData);
                setSuccessMsg("Expense created successfully");
            }

            setEditingExpenseId(null);
            setReport("");
            setExpenseDate("");
            setMerchant("");
            setCategory("");
            setAmount(0);
            setQuantity("");
            setWeight("");
            setDescription("");
            setPaidBy("");
            setStatus("UnPaid");
            setShowPopup(false);
        } catch (err) {
            setErrorMsg(err.message || "Server error. Please try again later.");
        }
    };

    useEffect(() => {
        if (successMsg) {
            const timer = setTimeout(() => setSuccessMsg(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [successMsg]);

    useEffect(() => {
        if (errorMsg) {
            const timer = setTimeout(() => setErrorMsg(""), 3000);
            return () => clearTimeout(timer);
        }
    }, [errorMsg]);

    const handleDelete = async (expenseId) => {
        if (!expenseId) return;

        if (!window.confirm("Are you sure you want to delete this expense?")) return;

        try {
            await deleteExpense.mutateAsync(expenseId);
            setSuccessMsg("Expense deleted successfully");

        } catch (err) {
            setErrorMsg(err.message || "Failed to delete expense");
        }
    };

    const [isDateApplied, setIsDateApplied] = useState(false);

    const filteredExpenses = expenses
        .filter(report => {
            if (!search) return true;
            return report.report.toLowerCase().includes(search.toLowerCase());
        })
        .flatMap(report =>
            (report.expenses || [])
                .filter(exp => categoryFilter === "ALL" ? true : exp.category === categoryFilter)
                .filter(exp => !isDateApplied || isSameOrInRange(exp.expenseDate, dateRange.startDate, dateRange.endDate))
                .map(exp => ({ ...exp, reportName: report.report, userId: report.userId }))
        );

    const sortedExpenses = [...filteredExpenses].sort(
        (a, b) => new Date(b.expenseDate) - new Date(a.expenseDate)
    );

    const totalPages = Math.ceil(sortedExpenses.length / formsPerPage);

    const currentForms = sortedExpenses.slice(
        (currentPage - 1) * formsPerPage,
        currentPage * formsPerPage
    );

    const totalExpense = filteredExpenses.reduce(
        (sum, exp) => sum + Number(exp.amount || 0),
        0
    );

    const handlePageChange = (page) => setCurrentPage(page);

    const truncate = (text, num) => {
        if (!text) return "";

        return text.length > num ? text.slice(0, num) + " ... " : text
    }

    useEffect(() => {
        setCurrentPage(1);
    }, [search, categoryFilter, isDateApplied, dateRange]);

    const isLoading = createExpense.isPending || updateExpense.isPending;

    if (fetching) {
        return (
            <div className="relative h-[90.7vh] p-2 rs:p-6 overflow-y-auto">
                <SkeletonPage />
            </div>
        );
    }

    if (!canViewHome) {
        return (
            <div>
                <div className="relative h-[90.7vh] overflow-hidden z-1">
                    <PermissionPopup />
                </div>
            </div>
        );
    }

    return (
        <div className="relative p-2 rs:p-6">
            <div className="flex flex-col xl:flex-row xl:justify-between xl:items-center gap-3 mb-4">

                {/* Title */}
                <h2 className="text-[15px] sm:text-[20px] font-bold text-gray-800 whitespace-nowrap">
                    Expense
                </h2>

                {/* Right Controls */}
                <div className="flex flex-col sm:flex-row flex-wrap xl:flex-nowrap gap-3 w-full xl:w-auto xl:justify-end">

                    <div className="flex flex-row gap-3 w-full sm:w-auto">
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search Invoice name"
                            className="px-2 py-1 text-xs sm:text-sm lg:text-sm lg:px-3 lg:py-2 w-full sm:w-[220px]"
                        />

                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="border rounded px-2 py-2 text-xs sm:text-sm lg:text-sm lg:px-3 lg:py-2 w-full sm:w-[180px]"
                        >
                            <option value="ALL">All</option>
                            <option value="Electricity Bill">Electricity Bill</option>
                            <option value="Water Bill">Water Bill</option>
                            <option value="Office Rent">Office Rent</option>
                            <option value="Web Utilities">Web Utilities</option>
                            <option value="Office Travel">Office Travel</option>
                            <option value="Internet Bill">Internet Bill</option>
                            <option value="Stationery & Supplies">Stationery & Supplies</option>
                            <option value="Business Operating">Business Operating</option>
                            <option value="Maintenance">Maintenance</option>
                            <option value="Marketing">Marketing</option>
                            <option value="Office pantry & Refreshments">Office pantry & Refreshments</option>
                            <option value="Office Maintenance & general expenses">Office Maintenance & general expenses</option>
                        </select>
                    </div>
                    <div>
                        <div
                            className="cursor-pointer  inline-flex border p-1 md:p-[9px] rounded-md text-sm md:text-base bg-white  gap-2"
                            onClick={() => setShowDatePicker(true)}
                        >
                            <CalendarRange className="w-4 h-4 md:w-5 md:h-5" />
                            <span className="text-[10px] md:text-sm whitespace-nowrap">
                                {format(dateRange.startDate, "dd-MM-yyyy")} →{" "}
                                {format(dateRange.endDate, "dd-MM-yyyy")}
                            </span>
                        </div>

                        <DateRangePicker
                            open={showDatePicker}
                            selectedRange={dateRange}
                            onApply={(range) => {
                                if (range.startDate && range.endDate) {
                                    setDateRange(range);
                                    setIsDateApplied(true);
                                }
                            }}
                            onClose={() => setShowDatePicker(false)}
                        />
                    </div>
                    {/* Button */}
                    <div>
                        <Button
                            onClick={handleOpen}
                        >
                            + New Expense
                        </Button>
                    </div>

                </div>
            </div>
            <div className="border rounded-lg overflow-hidden bg-white">
                <table className="w-full text-sm text-left text-gray-700">
                    <thead className="bg-gray-50 text-xs  text-gray-500">
                        <tr>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">#</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm hidden zy:table-cell">EXPENSE Date</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm hidden rs:table-cell">MERCHANT</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm hidden zx:table-cell">AMOUNT</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">INVOICE NAME</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 w-[300px] text-[11px] sm:text-sm hidden custom:table-cell">DESCRIPTION</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm hidden rs:table-cell">CREATED AT</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">STATUS</th>
                            <th className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">ACTIONS</th>
                        </tr>
                    </thead>

                    <tbody>
                        {!fetching && filteredExpenses.length === 0 && (
                            <tr>
                                <td colSpan={8} className="text-center px-4 py-4 text-gray-500">
                                    No expenses found.
                                </td>
                            </tr>
                        )}

                        {!fetching && filteredExpenses.length > 0 && [...currentForms].map((exp, index) => (
                            <tr
                                key={exp._id}
                                className="hover:bg-gray-50  border-t"
                            >
                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">
                                    {(currentPage - 1) * formsPerPage + index + 1}
                                </td>
                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm hidden zy:table-cell">
                                    <p className="text-gray-800">{new Date(exp.expenseDate).toLocaleDateString("en-GB")}</p>
                                    <p className="text-blue-600 font-medium truncate max-w-[100px] hidden custom:table-cell">{exp.category}</p>
                                </td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm truncate max-w-[150px] hidden rs:table-cell">{exp.merchant || "—"}</td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm font-medium text-gray-900 hidden zx:table-cell">
                                    ₹{Number(exp.amount || 0).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}                                </td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">
                                    <p className="font-medium text-gray-800">{exp.reportName}</p>
                                    <p className="text-xs text-gray-400 hidden rs:table-cell">{exp.userId}</p>
                                </td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm w-[300px] hidden custom:table-cell" ><p>{exp.description ? truncate(exp.description, 30) : '-'}</p></td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm hidden rs:table-cell">
                                    <p className="text-gray-800">{new Date(exp.createdAt).toLocaleDateString("en-GB")}</p>
                                </td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm text-left">
                                    <span className={`px-2 py-1 text-[11px] sm:text-sm rounded-md font-medium ${exp.status === "Paid" ? "bg-green-100 text-green-700" : "bg-purple-100 text-purple-700"}`}>
                                        {exp.status === "Paid" ? "Paid" : "UnPaid"}
                                    </span>
                                </td>

                                <td className="px-2 py-1 sm:px-2 sm:py-2 text-[11px] sm:text-sm">
                                    <div className="flex gap-0 sm:gap-[5px] items-center">
                                        <div className="p-1 rounded cursor-pointer hover:bg-[#e0d4e5]" onClick={(e) => { e.stopPropagation(); setPreviewExpenses(exp); }}>
                                            <Eye size={16} color="#ab54d0" /></div>
                                        <div className="p-1 rounded cursor-pointer hover:bg-blue-100" onClick={() => handleEdit(exp)}>
                                            <Pencil
                                                size={16}
                                                color="blue"
                                                className="cursor-pointer"
                                            />
                                        </div>
                                        <div className="p-1 rounded cursor-pointer hover:bg-red-100" onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete(exp._id);
                                        }} >
                                            <Trash2 size={16} color="red" className="cursor-pointer" />
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {previewExpenses && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2 sm:px-4" onClick={() => setPreviewExpenses(null)}>
                    <div
                        className="bg-white relative rounded-lg w-full max-w-[700px] shadow-lg h-[80vh] overflow-hidden animate-scaleIn"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 px-2 py-1 sm:px-2 sm:py-2">
                            <h2 className="text-white text-[15px] sm:text-[20px]">
                                Expense Detail
                            </h2>

                            <button
                                className="text-white hover:text-red-400"
                                onClick={() => setPreviewExpenses(null)}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="w-full  border-gray-200 rounded-md p-2 sm:p-6 bg-white shadow-sm">
                            <table className="w-full border border-gray-200 rounded-md mb-4 sm:mb-6">
                                <tbody className="divide-y divide-gray-200">
                                    <InfoRow label="User ID" value={previewExpenses.merchant || "—"} />
                                    <InfoRow label="Invoice Name" value={previewExpenses.reportName || "—"} />
                                    <InfoRow label="Amount" value={`₹${previewExpenses.amount?.toLocaleString() || "0"}`} />
                                    <InfoRow
                                        label="Date"
                                        value={new Date(previewExpenses.expenseDate).toLocaleDateString("en-GB")}
                                    />
                                    <InfoRow
                                        label="Created At"
                                        value={new Date(previewExpenses.createdAt).toLocaleDateString("en-GB")}
                                    />
                                    <InfoRow label="Paid By" value={previewExpenses.paidBy || "—"} />
                                    <InfoRow label="Category" value={previewExpenses.category || "—"} />
                                    <InfoRow label="Description" value={previewExpenses.description || "—"} />
                                    <InfoRow label="Quantity" value={previewExpenses.quantity || "—"} />
                                    <InfoRow label="Weight" value={previewExpenses.weight || "—"} />
                                    <InfoRow
                                        label="Status"
                                        value={
                                            <span
                                                className={`px-2 py-1 text-xs rounded ${previewExpenses.status === "Paid"
                                                    ? "bg-green-100 text-green-700"
                                                    : "bg-purple-100 text-purple-700"
                                                    }`}
                                            >
                                                {previewExpenses.status}
                                            </span>
                                        }
                                    />
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                handlePageChange={handlePageChange}
            />

            <div className="my-8">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white
                     rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300 transform">
                    <div className="flex items-center justify-between">
                        <div className="flex gap-2 items-center">
                            <HandCoins className="w-8 h-8 sm:h-12 sm:w-12 opacity-80" />
                            <p className="text-blue-100 text-[16px] sm:text-[20px] lg:text-[25px]">Total Expense</p>
                        </div>
                        <p className="text-[16px] sm:text-2xl lg:text-3xl font-bold">
                            {fetching
                                ? "..."
                                : "₹" + totalExpense.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                        </p>
                    </div>
                </div>
            </div>


            <div
                className={`fixed inset-0 z-50 transition-opacity duration-300 ${showPopup ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                    }`}
            >
                <div
                    className="absolute inset-0 bg-black/40"
                    onClick={() => setShowPopup(false)}
                />

                <div
                    className={`absolute top-0 right-0 h-full w-full sm:w-[420px] bg-white shadow-xl
    transform transition-transform duration-300 ease-in-out
    ${showPopup ? "translate-x-0" : "translate-x-full"}`}
                >
                    <div className="p-2 sm:p-6 overflow-y-auto h-full">
                        {/* Header */}
                        <div className="flex justify-between items-center mb-3 sm:mb-6 border-b pb-3">
                            <h3 className="text-[14px] sm:text-[18px] ">New Expense</h3>
                            <button
                                onClick={() => setShowPopup(false)}
                                className="text-gray-500 hover:text-black transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Form */}
                        <form className="space-y-5" onSubmit={handleSubmit}>
                            {/* Invoice */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Invoice</label>
                                <Input
                                    type="text"
                                    value={report}
                                    onChange={(e) => setReport(e.target.value)}
                                    required
                                    maxLength={30}
                                    className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                />
                            </div>

                            {/* Expense Date */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Expense Date</label>
                                <Input
                                    type="date"
                                    value={expenseDate ? new Date(expenseDate).toISOString().split("T")[0] : ""}
                                    onChange={(e) => setExpenseDate(e.target.value)}
                                    required
                                    max={new Date().toISOString().split("T")[0]}
                                    className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                />
                            </div>

                            {/* Merchant */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Merchant</label>
                                <Input
                                    value={merchant}
                                    onChange={(e) => setMerchant(e.target.value)}
                                    maxLength={50}
                                    className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                />
                            </div>

                            {/* Category */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Category</label>
                                <Select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    options={[
                                        "Electricity Bill",
                                        "Water Bill",
                                        "Office Rent",
                                        "Web Utilities",
                                        "Office Travel",
                                        "Internet Bill",
                                        "Stationery & Supplies",
                                        "Business Operating",
                                        "Maintenance",
                                        "Marketing",
                                        "Office pantry & Refreshments",
                                        "Office Maintenance & general expenses",
                                    ]}
                                    className="w-full border p-2 rounded text-[12px] sm:text-[13px]"
                                />
                            </div>

                            {/* Quantity & Weight */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Quantity</label>
                                    <Input
                                        type="number"
                                        value={quantity}
                                        placeholder="Enter Quantity"
                                        onChange={(e) => setQuantity(e.target.value)}
                                        className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Weight</label>
                                    <Input
                                        type="text"
                                        value={weight}
                                        placeholder="Enter Weight"
                                        onChange={(e) => setWeight(e.target.value)}
                                        className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                    />
                                </div>
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Amount INR</label>
                                <Input
                                    type="text"
                                    value={amount}
                                    onChange={(e) => {
                                        let val = e.target.value;
                                        val = val.replace(/[^0-9.]/g, "");
                                        const parts = val.split(".");
                                        if (parts.length > 2) {
                                            val = parts[0] + "." + parts.slice(1).join("");
                                        }
                                        const [intPart, decPart] = val.split(".");
                                        if (intPart?.length > 7) return;
                                        if (decPart?.length > 2) val = intPart + "." + decPart.slice(0, 2);

                                        setAmount(val);
                                    }}
                                    placeholder="Enter Amount"
                                    className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Description</label>
                                <textarea
                                    className="w-full text-[12px] sm:text-[13px] border border-gray-300 rounded-md p-2 focus:ring-1 focus:ring-gray-900 focus:outline-none resize-none"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    maxLength={500}
                                    rows={3}
                                />
                            </div>

                            {/* Paid By */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Paid By</label>
                                <Input
                                    type="text"
                                    value={paidBy}
                                    onChange={(e) => setPaidBy(e.target.value)}
                                    placeholder="Paid By"
                                    className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="block text-[12px] sm:text-[13px] font-medium mb-1">Status</label>
                                <Select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    options={["UnPaid", "Paid"]}
                                    className="w-full border p-2 rounded text-[12px] sm:text-[12px]"
                                />
                            </div>

                            {/* Submit */}
                            <Button
                                type="submit"
                                variant="success"
                                disabled={isLoading}
                                className="flex items-center justify-center gap-2"
                            >
                                {isLoading ? (
                                    <>
                                        Submitting...
                                        <span className="loader-circle"></span>
                                    </>
                                ) : editingExpenseId ? (
                                    "Update Expense"
                                ) : (
                                    "Submit Expense"
                                )}
                            </Button>

                            {/* Messages */}
                            {successMsg && <p className="text-green-600 text-sm mt-2">{successMsg}</p>}
                            {errorMsg && <p className="text-red-600 text-sm mt-2">{errorMsg}</p>}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}
